import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmt, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Calculator, Trash2, ChevronRight } from "lucide-react";

export default function Calculations() {
  const [calcs, setCalcs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", product_code: "", author: "", description: "" });
  const nav = useNavigate();

  const load = useCallback(() => api.get("/calculations").then((r) => setCalcs(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Zadejte název výrobku"); return; }
    const r = await api.post("/calculations", { ...form, author: form.author || "Neznámý" });
    toast.success("Kalkulace vytvořena");
    setOpen(false);
    setForm({ name: "", product_code: "", author: "", description: "" });
    nav(`/kalkulace/${r.data.id}`);
  };

  const del = async (id) => {
    await api.delete(`/calculations/${id}`);
    toast.success("Kalkulace smazána");
    load();
  };

  return (
    <div className="fade-up">
      <PageHeader title="Kalkulace" subtitle="Kalkulace nákladů výrobků">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="calc-new-button">
              <Plus className="h-4 w-4 mr-2" /> Nová kalkulace
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">Nová kalkulace výrobku</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="name">Název výrobku *</Label>
                <Input id="name" data-testid="calc-form-name" className="mt-2" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="např. Salát Caesar 250g" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="code">Kód výrobku</Label>
                  <Input id="code" data-testid="calc-form-code" className="mt-2" value={form.product_code}
                    onChange={(e) => setForm({ ...form, product_code: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="author">Vytvořil</Label>
                  <Input id="author" data-testid="calc-form-author" className="mt-2" value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Jméno" />
                </div>
              </div>
              <div>
                <Label htmlFor="desc">Popis</Label>
                <Textarea id="desc" data-testid="calc-form-desc" className="mt-2" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Zrušit</Button>
              <Button onClick={create} data-testid="calc-form-submit">Vytvořit a upravit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="px-10 py-8">
        {calcs.length === 0 ? (
          <div className="bg-white border border-border rounded-xl px-6 py-16 text-center">
            <Calculator className="h-10 w-10 mx-auto text-muted-foreground/50" />
            <div className="mt-4 font-medium">Zatím žádné kalkulace</div>
            <div className="text-sm text-muted-foreground mt-1">Vytvořte první kalkulaci výrobku.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {calcs.map((c) => (
              <div key={c.id} data-testid={`calc-card-${c.id}`}
                className="group bg-white border border-border rounded-xl p-6 hover:-translate-y-1 hover:shadow-sm transition-transform duration-200 cursor-pointer relative"
                onClick={() => nav(`/kalkulace/${c.id}`)}>
                <div className="flex items-start justify-between">
                  <div className="pr-8">
                    <div className="font-display text-lg font-medium leading-tight">{c.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {c.product_code ? `${c.product_code} · ` : ""}{c.author}
                    </div>
                    {(c.customer_name || c.recipe_name) && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {c.customer_name && <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary/10 text-secondary" data-testid={`calc-customer-${c.id}`}>{c.customer_name}</span>}
                        {c.recipe_name && <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">receptura: {c.recipe_name}</span>}
                      </div>
                    )}
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button onClick={(e) => e.stopPropagation()} data-testid={`calc-delete-${c.id}`}
                        className="text-muted-foreground hover:text-destructive p-1">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Smazat kalkulaci?</AlertDialogTitle>
                        <AlertDialogDescription>Kalkulace „{c.name}" bude trvale odstraněna.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Zrušit</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(c.id)} data-testid={`calc-confirm-delete-${c.id}`}>Smazat</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <div className="mt-5 pt-5 border-t border-border flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-primary/80 font-semibold">Cena za 1 ks</div>
                    <div className="num text-2xl font-bold text-primary" data-testid={`calc-card-perks-${c.id}`}>
                      {fmt(c.total / (c.portions || 1))} Kč
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      dávka {fmt(c.total)} Kč · {fmt(c.portions || 1, 0)} ks · {c.items?.length || 0} položek
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                </div>
                <div className="text-[11px] text-muted-foreground mt-3">Změna {fmtDate(c.updated_at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
